import { z } from '@customerio/hydra-validation';
import plur from 'plur';
import { ImportType } from '@/services/db/sql-import/entity';
import { isEmptyString } from '@/utils/string';
import { DEFAULT_INTERVAL_SECONDS, type SqlImportDraft } from './draft';

/** The units the interval can be entered in; the wire carries seconds. */
export const INTERVAL_UNITS = ['minute', 'hour', 'day'] as const;
export type IntervalUnit = (typeof INTERVAL_UNITS)[number];

const SECONDS_PER_UNIT = { minute: 60, hour: 3600, day: 86400 } as const satisfies Record<IntervalUnit, number>;

export { DEFAULT_INTERVAL_SECONDS };

/** The identifier an object import matches rows on. */
export const OBJECT_ID_FIELD = 'object_id';

/** The identifiers a people import can match rows on. */
export const PEOPLE_ID_FIELDS = [
  { value: 'id', label: 'By id' },
  { value: 'email', label: 'By email' },
] as const;

/** The sync target value that means "people"; any other value is an object type id. */
export const PEOPLE_TARGET = 'people';

/** Unit options with the plural that matches the count typed next to them. */
export function intervalUnitOptions(count: number): { value: IntervalUnit; label: string }[] {
  return INTERVAL_UNITS.map((unit) => ({ value: unit, label: plur(unit, count) }));
}

/**
 * Seconds as a count in the largest unit that divides them evenly, after stray seconds are
 * dropped (intervals are whole minutes). Zero reads as a blank count of minutes.
 */
export function intervalFromSeconds(seconds: number) {
  const whole = Math.floor(seconds / 60) * 60;
  const unit: IntervalUnit =
    whole === 0
      ? 'minute'
      : ([...INTERVAL_UNITS].reverse().find((candidate) => whole % SECONDS_PER_UNIT[candidate] === 0) ?? 'minute');
  return { count: whole / SECONDS_PER_UNIT[unit], unit };
}

/** A count of units as seconds; a blank or non-positive count is zero seconds. */
export function intervalToSeconds(count: string, unit: IntervalUnit): number {
  const parsed = Number(count);
  if (!/^\d+$/.test(count.trim()) || parsed <= 0) {
    return 0;
  }
  return parsed * SECONDS_PER_UNIT[unit];
}

export type SegmentChoice = 'new' | 'existing';

/** What the Settings form edits. The draft's wire fields are derived from it on every change. */
export type SettingsFormValues = {
  /** Kept as typed; parsed on submit. */
  intervalCount: string;
  intervalUnit: IntervalUnit;
  /** Unix seconds of the scheduled first run; 0 to start on activation. */
  startAt: number;
  /** `people`, or the id of the object type the import writes to. */
  syncTarget: string;
  /** The people identifier; blank until chosen. Ignored for an object import. */
  idFieldName: string;
  syncToSegment: boolean;
  segmentChoice: SegmentChoice;
  segmentName: string;
  segmentId: string;
  clearEmptyCells: boolean;
  ignoreSuppressErr: boolean;
};

type DefaultsOptions = {
  /** Without the permission to create segments, only an existing segment can be chosen. */
  canCreateSegment: boolean;
  /** A workspace that cannot identify people by email matches on id, whatever a saved draft says. */
  canIdentifyByEmail: boolean;
};

/** The identifier a people import starts with: the saved one, or id when email is not an option. */
export function defaultIdFieldName(saved: string | undefined, canIdentifyByEmail: boolean): string {
  return canIdentifyByEmail ? (saved ?? '') : 'id';
}

/** The form's starting values from the draft; a new draft syncs every hour. */
export function settingsFormDefaults(
  draft: SqlImportDraft,
  { canCreateSegment, canIdentifyByEmail }: DefaultsOptions,
): SettingsFormValues {
  const interval = intervalFromSeconds(
    draft.interval == null || draft.interval === 0 ? DEFAULT_INTERVAL_SECONDS : draft.interval,
  );
  const hasExistingSegment = !isEmptyString(draft.segment_id);
  return {
    intervalCount: String(interval.count),
    intervalUnit: interval.unit,
    startAt: draft.start_at ?? 0,
    syncTarget:
      draft.import_type === ImportType.object && !isEmptyString(draft.object_type_id)
        ? draft.object_type_id
        : PEOPLE_TARGET,
    idFieldName:
      draft.import_type === ImportType.object ? '' : defaultIdFieldName(draft.id_field_name, canIdentifyByEmail),
    syncToSegment: draft.import_type === ImportType.workspaceAndSegment,
    segmentChoice: hasExistingSegment || !canCreateSegment ? 'existing' : 'new',
    segmentName: draft.segment_name ?? '',
    segmentId: draft.segment_id ?? '',
    clearEmptyCells: draft.clear_empty_cells ?? false,
    ignoreSuppressErr: draft.ignore_suppress_err ?? true,
  };
}

export const START_AT_MESSAGE = 'The scheduled time needs to be in the future.';
export const ID_FIELD_MESSAGE = "Attribute can't be blank.";
export const SEGMENT_NAME_MESSAGE = "Segment name can't be blank.";
export const SEGMENT_MESSAGE = "Segment can't be blank.";
export const INTERVAL_MESSAGE = 'Must be a positive number.';

/** The step's rules, checked when it is submitted. `nowInSeconds` is read at that moment. */
export function settingsFormSchema(nowInSeconds: () => number) {
  return z
    .object({
      intervalCount: z.string(),
      intervalUnit: z.enum(INTERVAL_UNITS),
      startAt: z.number(),
      syncTarget: z.string(),
      idFieldName: z.string(),
      syncToSegment: z.boolean(),
      segmentChoice: z.enum(['new', 'existing']),
      segmentName: z.string(),
      segmentId: z.string(),
      clearEmptyCells: z.boolean(),
      ignoreSuppressErr: z.boolean(),
    })
    .superRefine((values, ctx) => {
      refineSchedule(values, nowInSeconds(), ctx);
      if (values.syncTarget === PEOPLE_TARGET) {
        refinePeople(values, ctx);
      }
    });
}

function refineSchedule(values: SettingsFormValues, nowInSeconds: number, ctx: z.RefinementCtx) {
  if (intervalToSeconds(values.intervalCount, values.intervalUnit) === 0) {
    ctx.addIssue({ code: 'custom', path: ['intervalCount'], message: INTERVAL_MESSAGE });
  }
  if (values.startAt > 0 && values.startAt <= nowInSeconds) {
    ctx.addIssue({ code: 'custom', path: ['startAt'], message: START_AT_MESSAGE });
  }
}

function refinePeople(values: SettingsFormValues, ctx: z.RefinementCtx) {
  if (isEmptyString(values.idFieldName)) {
    ctx.addIssue({ code: 'custom', path: ['idFieldName'], message: ID_FIELD_MESSAGE });
  }
  if (!values.syncToSegment) {
    return;
  }
  if (values.segmentChoice === 'new' && isEmptyString(values.segmentName)) {
    ctx.addIssue({ code: 'custom', path: ['segmentName'], message: SEGMENT_NAME_MESSAGE });
  }
  if (values.segmentChoice === 'existing' && isEmptyString(values.segmentId)) {
    ctx.addIssue({ code: 'custom', path: ['segmentId'], message: SEGMENT_MESSAGE });
  }
}

/**
 * The draft fields the form owns, derived from its values. An object import matches on
 * `object_id` and has no segment; a people import carries the chosen identifier and, when it
 * syncs to a segment, either the segment to reuse or the name of the one to create.
 */
export function settingsToDraft(values: SettingsFormValues): Partial<SqlImportDraft> {
  const interval = intervalToSeconds(values.intervalCount, values.intervalUnit);
  const base = {
    interval: interval === 0 ? undefined : interval,
    start_at: values.startAt,
    clear_empty_cells: values.clearEmptyCells,
    ignore_suppress_err: values.ignoreSuppressErr,
  };
  if (values.syncTarget !== PEOPLE_TARGET) {
    return {
      ...base,
      import_type: ImportType.object,
      object_type_id: values.syncTarget,
      id_field_name: OBJECT_ID_FIELD,
      segment_id: undefined,
      segment_name: undefined,
    };
  }
  const people = {
    ...base,
    object_type_id: undefined,
    id_field_name: isEmptyString(values.idFieldName) ? undefined : values.idFieldName,
  };
  if (!values.syncToSegment) {
    return { ...people, import_type: ImportType.workspace, segment_id: undefined, segment_name: undefined };
  }
  return {
    ...people,
    import_type: ImportType.workspaceAndSegment,
    segment_id: values.segmentChoice === 'existing' && !isEmptyString(values.segmentId) ? values.segmentId : undefined,
    segment_name:
      values.segmentChoice === 'new' && !isEmptyString(values.segmentName) ? values.segmentName.trim() : undefined,
  };
}

/** The analytics properties Ember sent when the Settings step was completed. */
export function settingsSubmittedProperties(values: SettingsFormValues) {
  return {
    Frequency: values.intervalUnit,
    Interval: Number(values.intervalCount),
    'Sync To Segment': values.syncTarget === PEOPLE_TARGET && values.syncToSegment,
    'Sync Target': values.syncTarget === PEOPLE_TARGET ? 'People' : 'Objects',
  };
}
